import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { htmlToPlainText } from 'shared-helpers/common';
import { Avatar, ModalButtonWithForm, SortableList } from 'components/common';

import type { CalendarEventFeedbackQuestion } from 'shared-types';

interface Props {
  disabled?: boolean;
  questions: CalendarEventFeedbackQuestion[];
  questionsOrderUrl: string;
  updateQuestions: () => Promise<void>;
}

const CalendarEventFeedbackQuestionsOrder = ({
  disabled = false,
  questions,
  questionsOrderUrl,
  updateQuestions
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, theme } = useReduxState();

  const [dirty, setDirty] = useState(false);
  const [questionIds, setQuestionIds] = useState(
    questions.map((question) => question.id)
  );

  const handleOrderChange = (keys: number[]) => {
    setQuestionIds(keys);
    if (!dirty) setDirty(true);
  };

  const handleSubmit = async () => {
    await api.put(questionsOrderUrl, { questionIds });

    await updateQuestions();

    setDirty(false);
    dispatch.addNotice({
      text: messages.commonEditOrderSuccess,
      type: 'success'
    });

    return true;
  };

  const renderQuestion = (questionId: number, index: number) => {
    const question = questions.find(({ id }) => id === questionId);

    return (
      <>
        <Avatar
          backgroundColor={theme.lightGreyDark}
          fontSize="1.4rem"
          style={{ flexShrink: 0, marginRight: 10 }}
        >
          {index + 1}
        </Avatar>

        <div className="question">{htmlToPlainText(question?.question)}</div>

        <style jsx>{`
          .question {
            display: -webkit-box;
            flex-grow: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
          }
        `}</style>
      </>
    );
  };

  return (
    <ModalButtonWithForm
      buttonDisabled={disabled || questions.length < 2}
      buttonIcon="arrows-alt-v"
      buttonIsDropdown={true}
      buttonProps={
        disabled
          ? { tooltip: messages.eventCalendarFeedbackQuestionsLockedTooltip }
          : undefined
      }
      buttonText={messages.commonEditOrder}
      contentStyle={{ padding: 0 }}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalTitle={messages.commonEditOrder}
      modalWidth="medium"
      onHide={() => setDirty(false)}
      onSubmit={handleSubmit}
      submitDisabled={!dirty}
    >
      {() => (
        <SortableList
          includeButtons={true}
          items={questions.map((question) => question.id)}
          itemWithBorder={true}
          listId="calendar-event-feedback-questions-order"
          onChange={handleOrderChange}
          renderItem={renderQuestion}
        />
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventFeedbackQuestionsOrder;
